import {
  CAPPED_GUEST_INFO_MODE,
  ExtractedFormComponent,
  FORM_COMPONENT_ID_ATTR,
  GuestInfoMode,
  findIncompleteFormComponents,
  getFormComponentDescriptor,
  guestInfoCeiling,
  isGuestAddableMode,
  parseGuestInfoMode,
} from '@besty/shared/formComponents';
import DOMPurify from 'dompurify';
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../i18n';

import {
  localizeFormComponentReason,
  mountFormComponentAddress,
  mountFormComponentTextInput,
  readFormComponentFromElement,
} from './formComponentMounts';

interface FormField {
  name: string;
  required: boolean;
  type?: string;
}

interface FormRendererProps {
  html: string;
  css: string;
  fieldsSchema: FormField[];
  disabled?: boolean;
  initialValues?: Record<string, any>;
  customHtml?: boolean;
  submissionScript?: string;
  numOfAdults?: number;
  /** Listing max occupancy minus the primary signer, for capped guest-info. */
  maxAccompanyingGuests?: number | null;
  /** BES-9331: registry components extracted server-side (labels translated). */
  formComponents?: ExtractedFormComponent[];
}

export interface FormRendererRef {
  collect: () => { ok: boolean; values: Record<string, any>; errors: string[] };
  disable: () => void;
}

// Base CSS that matches Layout styling with proper spacing
const BASE_CSS = `
  /* Reset and base styles to match Layout */
  :host, html, body { 
    font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial; 
    color: #111827; 
    margin: 0; 
    padding: 0; 
  }
  
  /* Grid layout for proper spacing */
  #gp-root {
    display: grid;
    gap: 12px;
  }
  
  /* Typography matching Layout */
  h1, h2, h3, h4, h5, h6 {
    margin: 0 0 10px;
    font-weight: 600;
    color: #111827;
  }
  
  p {
    margin: 0 0 1rem;
    color: #4b5563;
  }
  
  /* Labels */
  label {
    display: block;
    margin: 6px 0 4px;
    font-weight: 500;
    color: #374151;
  }
  
  /* Form controls styling to match Layout (text-like inputs only) */
  input[type="text"],
  input[type="email"],
  input[type="tel"],
  input[type="date"],
  input[type="number"],
  input[type="password"],
  select,
  textarea {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.5;
    color: #374151;
    background-color: white;
    box-sizing: border-box; /* avoid overflow to the card edge */
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
  
  input[type="text"]:focus,
  input[type="email"]:focus,
  input[type="tel"]:focus,
  input[type="date"]:focus,
  input[type="number"]:focus,
  input[type="password"]:focus,
  select:focus,
  textarea:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  input:disabled:not([type="checkbox"]):not([type="radio"]),
  select:disabled,
  textarea:disabled {
    background-color: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
  }
  
  input[type="checkbox"]:disabled,
  input[type="radio"]:disabled {
    cursor: not-allowed;
  }
  
  /* Radio group styling to match editor */
  .radio-options {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    flex-direction: column;
  }
  
  .radio-options label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-weight: 500;
    color: #374151;
    margin: 0;
  }
  
  .radio-options input[type="radio"] {
    width: auto;
    margin: 0;
  }
  
  /* Checkbox styling to match editor */
  [data-form-field="checkbox"] label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-weight: 500;
    color: #374151;
    margin: 0;
  }

  [data-form-field="checkbox"] input[type="checkbox"] {
    width: auto;
    margin: 0;
    padding: 0;
    border: 0; /* keep native look */
  }
  
  /* Checkbox group styling to match editor */
  .checkbox-options {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    flex-direction: column;
  }
  
  .checkbox-options label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-weight: 500;
    color: #374151;
    margin: 0;
  }
  
  .checkbox-options input[type="checkbox"] {
    width: auto;
    margin: 0;
    padding: 0;
    border: 0; /* keep native look */
  }
  
  /* Inline layout for option groups */
  .radio-options.inline,
  .checkbox-options.inline {
    flex-direction: row;
    align-items: center;
  }
  
  /* Validation error styling */
  .error {
    outline: 2px solid #ef4444 !important;
    outline-offset: 1px;
    border-color: #ef4444 !important;
  }
  
  /* Required asterisk styling */
  .required-asterisk {
    color: #ef4444;
    margin-left: 4px;
  }
  
  /* Inline error message styling */
  .error-text {
    color: #b91c1c;
    font-size: 12px;
    margin-top: 4px;
  }
`;

// Hydrate custom components that depend on editor-time logic
function hydrateCustomComponents(
  root: HTMLElement,
  options?: { initialValues?: Record<string, any>; numOfAdults?: number; maxAccompanyingGuests?: number | null },
) {
  // Hydrate radio groups
  const radioGroups = root.querySelectorAll('[data-form-field="radio-group"]');
  radioGroups.forEach((group) => {
    const container = group.querySelector('.radio-options');
    const labelEl = group.querySelector('label');
    if (!container) return;

    // Check if already has radio buttons
    if (container.querySelector('input[type="radio"]')) return;

    const options = group.getAttribute('data-options');
    const name = group.getAttribute('data-name');
    const inline = group.getAttribute('data-inline') === 'true';
    const required = group.getAttribute('data-required') === 'true';
    const labelText = group.getAttribute('data-label'); // NEW

    // NEW: enforce label if provided by traits
    if (labelEl && labelText) {
      labelEl.textContent = labelText;
    }

    if (!options || !name) return;

    // Clear container and rebuild
    container.innerHTML = '';
    if (inline) {
      container.classList.add('inline');
    }

    // Create radio options
    const optionList = options.split('\n').filter((opt) => opt.trim());
    optionList.forEach((option, index) => {
      const optionValue = option.trim();
      const value = optionValue.toLowerCase().replace(/[^a-z0-9]+/g, '_') || `option_${index + 1}`;

      const optionLabel = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = value;
      if (required) input.required = true;

      optionLabel.appendChild(input);
      optionLabel.appendChild(document.createTextNode(optionValue));
      container.appendChild(optionLabel);
    });
  });

  // Hydrate checkbox groups
  const checkboxGroups = root.querySelectorAll('[data-form-field="checkbox-group"]');
  checkboxGroups.forEach((group) => {
    const container = group.querySelector('.checkbox-options');
    const labelEl = group.querySelector('label');
    if (!container) return;

    // Check if already has checkboxes
    if (container.querySelector('input[type="checkbox"]')) return;

    const options = group.getAttribute('data-options');
    const name = group.getAttribute('data-name');
    const inline = group.getAttribute('data-inline') === 'true';
    const labelText = group.getAttribute('data-label');

    // Enforce label if provided by traits
    if (labelEl && labelText) {
      labelEl.textContent = labelText;
    }

    if (!options || !name) return;

    // Clear container and rebuild
    container.innerHTML = '';
    if (inline) {
      container.classList.add('inline');
    }

    // Create checkbox options
    const optionList = options.split('\n').filter((opt) => opt.trim());
    optionList.forEach((option, index) => {
      const optionValue = option.trim();
      const value = optionValue.toLowerCase().replace(/[^a-z0-9]+/g, '_') || `option_${index + 1}`;

      const optionLabel = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = name;
      input.value = value;
      // NOTE: Do NOT set required on individual checkboxes in a group
      // HTML required on each checkbox means "all must be checked", not "at least one"
      // Our custom validation handles this correctly

      const span = document.createElement('span');
      span.textContent = optionValue;

      optionLabel.appendChild(input);
      optionLabel.appendChild(span);
      container.appendChild(optionLabel);
    });
  });

  // Hydrate select fields
  const selectFields = root.querySelectorAll('[data-form-field="select"]');
  selectFields.forEach((field) => {
    const select = field.querySelector('select');
    const labelEl = field.querySelector('label');
    if (!select) return;

    const options = field.getAttribute('data-options');
    const placeholder = field.getAttribute('data-placeholder');
    const name = field.getAttribute('data-name');
    const required = field.getAttribute('data-required') === 'true';
    const labelText = field.getAttribute('data-label'); // NEW

    // NEW: enforce label if provided by traits
    if (labelEl && labelText) {
      labelEl.textContent = labelText;
    }

    if (!name) return;

    // Always rebuild from traits to guarantee fidelity
    select.innerHTML = '';
    select.name = name;
    select.required = required;

    if (placeholder) {
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      placeholderOption.disabled = true;
      placeholderOption.selected = true;
      select.appendChild(placeholderOption);
    }

    (options || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((text, i) => {
        const value = text.toLowerCase().replace(/[^a-z0-9]+/g, '_') || `option_${i + 1}`;
        const el = document.createElement('option');
        el.value = value;
        el.textContent = text;
        select.appendChild(el);
      });
  });

  // Hydrate input-like fields (input, email, textarea, date, phone)
  const inputLikeFields = root.querySelectorAll(
    '[data-form-field="input"], [data-form-field="email"], [data-form-field="textarea"], [data-form-field="date"], [data-form-field="phone"]'
  );
  inputLikeFields.forEach((field) => {
    const labelEl = field.querySelector('label');
    const labelText = field.getAttribute('data-label');
    const placeholderText = field.getAttribute('data-placeholder');

    // Enforce label if provided by traits
    if (labelEl && labelText) {
      labelEl.textContent = labelText;
    }

    // Enforce placeholder if provided by traits
    if (placeholderText) {
      const inputEl = field.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;
      if (inputEl) {
        inputEl.placeholder = placeholderText;
      }
    }
  });

  // Hydrate checkbox fields (label is in a span)
  const checkboxFields = root.querySelectorAll('[data-form-field="checkbox"]');
  checkboxFields.forEach((field) => {
    const spanEl = field.querySelector('span');
    const labelText = field.getAttribute('data-label');
    const inputEl = field.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
    const required = field.getAttribute('data-required') === 'true';
    const nameAttr = field.getAttribute('data-name');

    // Enforce label if provided by traits
    if (spanEl && labelText) {
      spanEl.textContent = labelText;
    }

    // Propagate name/value/required to the actual input
    if (inputEl) {
      if (nameAttr) inputEl.name = nameAttr; // critical: gives the checkbox its name
      if (!inputEl.getAttribute('value')) inputEl.value = 'yes';
      if (required) inputEl.required = true;
    }
  });

  // Set required attributes on inputs
  const inputs = root.querySelectorAll('[data-required="true"]');
  inputs.forEach((input) => {
    (input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).required = true;
  });

  // Set names on inputs from their parent wrapper's data-name attribute
  // This fixes templates where inputs have incorrect/duplicate names in the stored HTML
  const fieldWrappers = root.querySelectorAll('[data-form-field][data-name]');
  fieldWrappers.forEach((wrapper) => {
    const correctName = wrapper.getAttribute('data-name');
    if (!correctName) return;
    
    // Find the actual form control inside this wrapper
    const input = wrapper.querySelector('input, select, textarea');
    if (input && !(input as HTMLInputElement).type?.match(/^(checkbox|radio)$/)) {
      // For non-checkbox/radio inputs, set the name directly
      (input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).name = correctName;
    } else if (wrapper.getAttribute('data-form-field') === 'checkbox-group') {
      // For checkbox groups, set name on all checkboxes
      const checkboxes = wrapper.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((cb) => {
        (cb as HTMLInputElement).name = correctName;
      });
    } else if (wrapper.getAttribute('data-form-field') === 'radio-group') {
      // For radio groups, set name on all radios
      const radios = wrapper.querySelectorAll('input[type="radio"]');
      radios.forEach((radio) => {
        (radio as HTMLInputElement).name = correctName;
      });
    } else if (wrapper.getAttribute('data-form-field') === 'checkbox' && input) {
      // For single checkboxes, set the name
      (input as HTMLInputElement).name = correctName;
    }
  });

  // Add required asterisks to labels
  const requiredWrappers = root.querySelectorAll('[data-form-field][data-required="true"]');
  requiredWrappers.forEach((wrapper) => {
    const label = wrapper.querySelector('label');
    if (label && !label.querySelector('.required-asterisk')) {
      const asterisk = document.createElement('span');
      asterisk.className = 'required-asterisk';
      asterisk.textContent = '*';
      label.appendChild(asterisk);
    }
  });

  // Ensure radio groups have required on all radio inputs when group is required
  const requiredRadioGroups = root.querySelectorAll('[data-form-field="radio-group"][data-required="true"]');
  requiredRadioGroups.forEach((group) => {
    const radios = group.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      (radio as HTMLInputElement).required = true;
    });
  });

  // NOTE: We do NOT set required on checkbox groups because HTML required on each
  // checkbox means "all must be checked", not "at least one must be checked".
  // Our custom validation handles required checkbox groups correctly.

  // Hydrate guest-info components: replace editor placeholders with interactive
  // form sections where guests can add/fill accompanying guest data.
  hydrateGuestInfoComponents(root, options?.initialValues, options?.numOfAdults, options?.maxAccompanyingGuests);
}

// CSS for guest-info sections injected once into the shadow root
const GUEST_INFO_CSS = `
  .gi-section { margin: 16px 0; }
  .gi-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
  .gi-title { font-weight: 600; font-size: 14px; color: #1e293b; }
  .gi-add-btn {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    width: 100%; padding: 10px; border: 1px dashed #94a3b8; border-radius: 8px;
    background: #fff; color: #64748b; font-size: 14px; cursor: pointer;
    margin-top: 8px;
  }
  .gi-guest-card {
    border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden;
    margin: 12px 0; background: #fff;
  }
  .gi-guest-head {
    background: #f0f9ff; border-bottom: 1px solid #e2e8f0; padding: 10px 16px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .gi-guest-head-label { font-weight: 600; font-size: 14px; color: #1e293b; }
  .gi-remove-btn {
    border: none; background: none; color: #ef4444; font-size: 18px;
    cursor: pointer; padding: 2px 6px; border-radius: 4px;
  }
  .gi-remove-btn:hover { background: #fef2f2; }
  .gi-fields { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
  .gi-field label {
    display: block; font-size: 13px; color: #64748b; margin-bottom: 4px; font-weight: 500;
  }
  .gi-field input[type="text"],
  .gi-field input[type="email"],
  .gi-field input[type="tel"],
  .gi-field input[type="date"] {
    width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px;
    font-size: 14px; color: #374151; background: #fff; box-sizing: border-box;
  }
  .gi-field input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
  .gi-field .gi-checkbox-row { display: flex; align-items: center; gap: 8px; cursor: pointer; }
  .gi-empty { text-align: center; padding: 16px; color: #94a3b8; font-size: 14px; }
  .gi-add-btn-disabled {
    border-color: #e2e8f0; background: #f9fafb; color: #cbd5e1; cursor: not-allowed;
  }
  .gi-max-hint { text-align: center; color: #94a3b8; font-size: 12px; margin-top: 6px; }
`;

interface GuestInfoConfig {
  id: string;
  mode: GuestInfoMode;
  label: string;
  fields: Array<{ type: string; label?: string; required: boolean }>;
}

// Decode GrapesJS HTML-encoded attribute values
function decodeHtmlAttr(str: string): string {
  return str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&');
}

function parseGuestInfoConfig(el: Element): GuestInfoConfig | null {
  const id = el.getAttribute('data-guest-info-id');
  if (!id) return null;
  const mode = parseGuestInfoMode(el.getAttribute('data-guest-mode'));
  const label = el.getAttribute('data-guest-label') || 'Accompanying Guest';
  let fields: GuestInfoConfig['fields'] = [];
  try {
    const raw = el.getAttribute('data-guest-fields') || '[]';
    fields = JSON.parse(decodeHtmlAttr(raw));
  } catch {
    // keep empty
  }
  return { id, mode, label, fields };
}

function inputTypeForField(fieldType: string): string {
  switch (fieldType) {
    case 'email': return 'email';
    case 'phone': return 'tel';
    case 'date': return 'date';
    default: return 'text';
  }
}

function buildGuestCard(
  config: GuestInfoConfig,
  guestIndex: number,
  removable: boolean,
  onRemove: () => void,
  prefill?: Record<string, unknown>,
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'gi-guest-card';
  card.setAttribute('data-gi-guest', `${config.id}-${guestIndex}`);

  // Header
  const head = document.createElement('div');
  head.className = 'gi-guest-head';
  const headLabel = document.createElement('span');
  headLabel.className = 'gi-guest-head-label';
  headLabel.textContent = `${config.label} ${guestIndex + 1}`;
  head.appendChild(headLabel);
  if (removable) {
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'gi-remove-btn';
    removeBtn.textContent = '\u00d7';
    removeBtn.title = 'Remove guest';
    removeBtn.addEventListener('click', onRemove);
    head.appendChild(removeBtn);
  }
  card.appendChild(head);

  // Fields
  const fieldsDiv = document.createElement('div');
  fieldsDiv.className = 'gi-fields';
  // Two fields with the same label would slug to the same key and clobber
  // each other in the submitted payload; suffix duplicates to keep them
  // distinct.
  const slugCounts = new Map<string, number>();
  config.fields.forEach((field) => {
    const fieldLabel = field.label || field.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const fieldType = field.type;

    // Skip complex types not supported in check-in forms
    if (['signature', 'initials', 'selfie', 'id_upload', 'image_upload'].includes(fieldType)) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'gi-field';

    const baseSlug = fieldLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const seen = slugCounts.get(baseSlug) ?? 0;
    slugCounts.set(baseSlug, seen + 1);
    const fieldSlug = seen === 0 ? baseSlug : `${baseSlug}_${seen + 1}`;
    const prefillValue = prefill ? prefill[fieldSlug] : undefined;

    if (fieldType === 'checkbox') {
      const labelEl = document.createElement('label');
      labelEl.className = 'gi-checkbox-row';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = `gi_${config.id}_g${guestIndex}_${fieldType}_${fieldSlug}`;
      if (field.required) input.required = true;
      if (prefillValue === true) input.checked = true;
      const span = document.createElement('span');
      span.textContent = fieldLabel + (field.required ? ' *' : '');
      labelEl.appendChild(input);
      labelEl.appendChild(span);
      wrapper.appendChild(labelEl);
    } else {
      const labelEl = document.createElement('label');
      labelEl.textContent = fieldLabel + (field.required ? ' *' : '');
      wrapper.appendChild(labelEl);
      const input = document.createElement('input');
      input.type = inputTypeForField(fieldType);
      input.name = `gi_${config.id}_g${guestIndex}_${fieldType}_${fieldSlug}`;
      input.placeholder = fieldLabel;
      if (field.required) input.required = true;
      if (typeof prefillValue === 'string' || typeof prefillValue === 'number') {
        input.value = String(prefillValue);
      }
      wrapper.appendChild(input);
    }
    fieldsDiv.appendChild(wrapper);
  });
  card.appendChild(fieldsDiv);
  return card;
}

function hydrateGuestInfoComponents(
  root: HTMLElement,
  initialValues?: Record<string, any>,
  numOfAdults?: number,
  maxAccompanyingGuests?: number | null,
) {
  const guestInfoEls = root.querySelectorAll('[data-guest-info-id]');
  if (guestInfoEls.length === 0) return;

  guestInfoEls.forEach((el) => {
    const config = parseGuestInfoConfig(el);
    if (!config) return;

    // Replace placeholder content
    el.innerHTML = '';
    (el as HTMLElement).style.cssText =
      'margin: 16px 0; padding: 0; border: none; background: transparent; text-align: left; min-height: 0;';

    const section = document.createElement('div');
    section.className = 'gi-section';
    section.setAttribute('data-gi-section', config.id);

    // Container for guest cards
    const cardsContainer = document.createElement('div');
    cardsContainer.setAttribute('data-gi-cards', config.id);

    let guestCounter = 0;
    // Only capped mode with a known listing capacity limits the card count
    // here. Manual check-in forms have never had a limit, and capped falls back
    // to manual when the capacity lookup comes back empty, so both stay
    // unbounded.
    const ceiling =
      config.mode === CAPPED_GUEST_INFO_MODE && typeof maxAccompanyingGuests === 'number'
        ? guestInfoCeiling(config.mode, maxAccompanyingGuests)
        : Infinity;
    el.setAttribute('data-gi-ceiling', String(ceiling));

    const addGuest = (prefill?: Record<string, unknown>, removable = true) => {
      const idx = guestCounter++;
      const card = buildGuestCard(
        config,
        idx,
        removable,
        () => {
          card.remove();
          // Re-number headers
          const cards = cardsContainer.querySelectorAll('.gi-guest-card');
          cards.forEach((c, i) => {
            const lbl = c.querySelector('.gi-guest-head-label');
            if (lbl) lbl.textContent = `${config.label} ${i + 1}`;
          });
          cardsContainer.dispatchEvent(new Event('gi-card-removed'));
        },
        prefill,
      );
      cardsContainer.appendChild(card);
    };

    section.appendChild(cardsContainer);

    // Saved values from a prior submission take precedence: rehydrate the same
    // number of cards with their original answers so revisits show what the
    // guest entered.
    const savedGuests = initialValues?.[`guest_info_${config.id}`];
    if (Array.isArray(savedGuests) && savedGuests.length > 0) {
      savedGuests.forEach((guest) => {
        addGuest(
          guest != null && typeof guest === 'object'
            ? (guest as Record<string, unknown>)
            : undefined,
        );
      });
    } else if (config.mode === 'auto') {
      // Auto mode: one card per accompanying adult (party size minus the
      // primary signer). Default to 1 if we don't know the party size.
      // Cards are non-removable so guests cannot delete reservation-driven
      // entries to bypass validation.
      const accompanying =
        typeof numOfAdults === 'number' && Number.isFinite(numOfAdults)
          ? Math.max(0, numOfAdults - 1)
          : 1;
      for (let i = 0; i < accompanying; i++) addGuest(undefined, false);
    }

    // Add Guest button (guest-driven modes only). Capped mode disables it once
    // the listing's max occupancy minus the primary signer is reached.
    // BES-9726: a unit that sleeps 1 has a ceiling of 0, so there is nothing to
    // add and no limit to explain. Drop the control instead of showing it dead.
    if (isGuestAddableMode(config.mode) && ceiling > 0) {
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'gi-add-btn';
      addBtn.innerHTML = `+ ${i18n.t('Add Guest', { ns: 'portal' })}`;
      // At the ceiling the button stays visible but greyed out, with a tooltip
      // and a hint line explaining why. Only capped mode has a ceiling here, and
      // it comes from the listing.
      const maxedMessage = i18n.t('You have added the maximum number of guests allowed for this listing', {
        ns: 'portal',
      });
      const hint = document.createElement('div');
      hint.className = 'gi-max-hint';
      hint.textContent = maxedMessage;
      const syncAddBtn = () => {
        const cards = cardsContainer.querySelectorAll('.gi-guest-card').length;
        const atCeiling = cards >= ceiling;
        addBtn.disabled = atCeiling;
        addBtn.classList.toggle('gi-add-btn-disabled', atCeiling);
        addBtn.title = atCeiling ? maxedMessage : '';
        hint.style.display = atCeiling ? '' : 'none';
      };
      addBtn.addEventListener('click', () => {
        if (cardsContainer.querySelectorAll('.gi-guest-card').length >= ceiling) return;
        addGuest();
        syncAddBtn();
      });
      cardsContainer.addEventListener('gi-card-removed', syncAddBtn);
      section.appendChild(addBtn);
      section.appendChild(hint);
      syncAddBtn();
    }

    el.appendChild(section);
  });
}

/**
 * Collect guest-info field values from the DOM and return a structured
 * JSON object keyed by component ID. Each component maps to an array
 * of guest objects where keys are field labels and values are the input values.
 */
function collectGuestInfoValues(root: HTMLElement): Record<string, Array<Record<string, string | boolean>>> {
  const result: Record<string, Array<Record<string, string | boolean>>> = {};

  const sections = root.querySelectorAll('[data-gi-section]');
  sections.forEach((section) => {
    const componentId = section.getAttribute('data-gi-section');
    if (!componentId) return;

    const guests: Array<Record<string, string | boolean>> = [];
    const cards = section.querySelectorAll('.gi-guest-card');
    cards.forEach((card) => {
      const guestData: Record<string, string | boolean> = {};
      const inputs = card.querySelectorAll('input');
      inputs.forEach((input) => {
        // Extract readable field label from the name pattern: gi_{id}_g{n}_{type}_{label}
        const parts = input.name.split('_');
        // field label is everything after gi_{id}_g{n}_{type}_
        const labelParts = parts.slice(4);
        const fieldKey = labelParts.join('_') || input.name;
        if (input.type === 'checkbox') {
          guestData[fieldKey] = input.checked;
        } else {
          guestData[fieldKey] = input.value || '';
        }
      });
      // Always push every rendered card, even if empty (e.g. a config with
      // only RA-only field types builds 0 inputs). Otherwise client and
      // server disagree on guest count and submit returns validation_failed.
      guests.push(guestData);
    });

    if (guests.length > 0) {
      result[`guest_info_${componentId}`] = guests;
    }
  });

  return result;
}

/**
 * Validate required guest-info fields. Returns an array of human-readable
 * labels for any required fields that are empty.
 */
function validateGuestInfoRequired(root: HTMLElement): string[] {
  const errors: string[] = [];

  // Clear stale error markers from previous attempts so a now-valid input
  // does not keep its red border.
  root.querySelectorAll('.gi-guest-card input').forEach((node) => {
    const input = node as HTMLInputElement;
    input.classList.remove('error');
    input.style.borderColor = '';
  });

  // Per-card required-input checks.
  const cards = root.querySelectorAll('.gi-guest-card');
  cards.forEach((card) => {
    const inputs = card.querySelectorAll('input[required]') as NodeListOf<HTMLInputElement>;
    inputs.forEach((input) => {
      const isEmpty = input.type === 'checkbox' ? !input.checked : !input.value.trim();
      if (isEmpty) {
        const labelEl = input.closest('.gi-field')?.querySelector('label');
        const label = labelEl?.textContent?.replace(/\s*\*\s*$/, '').trim() || input.placeholder || input.name;
        errors.push(label);
        input.classList.add('error');
        input.style.borderColor = '#ef4444';
      }
    });
  });

  // Component-level check: in manual mode, a block with required fields but
  // zero cards would otherwise submit successfully. Auto mode legitimately
  // resolves to zero cards on solo stays (numOfAdults <= 1), so it is not an
  // error there.
  const sections = root.querySelectorAll('[data-guest-info-id]');
  sections.forEach((el) => {
    const config = parseGuestInfoConfig(el);
    if (!config || !isGuestAddableMode(config.mode)) return;
    const hasCards = el.querySelectorAll('.gi-guest-card').length > 0;
    if (hasCards) return;
    // A capped component in a unit that sleeps one has no room for a guest, so
    // there is nothing to require.
    if (el.getAttribute('data-gi-ceiling') === '0') return;
    const hasRequired = config.fields.some(
      (f) =>
        f.required &&
        !['signature', 'initials', 'selfie', 'id_upload', 'image_upload'].includes(f.type),
    );
    if (hasRequired) {
      errors.push(`${config.label} (at least 1 required)`);
    }
  });

  return errors;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isJsonSafeValue(value: unknown, seen = new WeakSet<object>()): boolean {
  if (value === null) return true;

  switch (typeof value) {
    case 'string':
    case 'boolean':
      return true;
    case 'number':
      return Number.isFinite(value);
    case 'object':
      if (seen.has(value)) return false;
      seen.add(value);
      if (Array.isArray(value)) {
        return value.every((item) => isJsonSafeValue(item, seen));
      }
      if (!isPlainObject(value)) return false;
      return Object.values(value).every((item) => isJsonSafeValue(item, seen));
    default:
      return false;
  }
}

function validateCustomSubmissionResult(result: unknown): string | null {
  if (result && typeof (result as any).then === 'function') {
    return 'Submission script returned a Promise; async scripts are not supported';
  }
  if (!isPlainObject(result)) {
    return 'Submission script must return a JSON object';
  }
  if (!isJsonSafeValue(result)) {
    return 'Submission script returned values that cannot be stored as JSON';
  }
  return null;
}

const FormRenderer = forwardRef<FormRendererRef, FormRendererProps>(
  (
    {
      html,
      css,
      fieldsSchema,
      disabled = false,
      initialValues,
      customHtml,
      submissionScript,
      numOfAdults,
      maxAccompanyingGuests,
      formComponents,
    },
    ref
  ) => {
    const { t } = useTranslation('portal');
    const hostRef = useRef<HTMLDivElement | null>(null);
    const shadowRef = useRef<ShadowRoot | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    // BES-9331: registry form components (phone, address). Mounted by attribute
    // sweep like the legacy hydrators; values live in a ref keyed by component
    // id and are merged into the submission, so the values blob always carries
    // every component's key (blank = ''), matching the BES-8799 contract.
    const formComponentFieldsRef = useRef<ExtractedFormComponent[]>([]);
    const formComponentValuesRef = useRef<Record<string, string>>({});
    const formComponentFlushersRef = useRef<Array<() => void | Promise<void>>>([]);

    const mountFormComponents = (root: HTMLElement) => {
      formComponentFieldsRef.current = [];
      formComponentValuesRef.current = {};
      formComponentFlushersRef.current = [];

      root.querySelectorAll<HTMLElement>(`[${FORM_COMPONENT_ID_ATTR}]`).forEach((el) => {
        const domField = readFormComponentFromElement(el);
        const descriptor = domField ? getFormComponentDescriptor(domField.type) : undefined;
        // Types this surface doesn't support (e.g. an attachment marker pasted
        // from an agreement template) hide instead of showing a dead editor
        // placeholder, and are excluded from validation.
        if (!domField || !descriptor || !descriptor.surfaces.includes('checkin-form')) {
          el.style.display = 'none';
          return;
        }

        // Prefer the server-extracted copy: its label is translated.
        const serverField = formComponents?.find((f) => f.id === domField.id);
        const field = serverField || domField;

        const initialRaw = initialValues?.[field.id];
        const initialValue = typeof initialRaw === 'string' ? initialRaw : '';
        formComponentValuesRef.current[field.id] = initialValue;
        formComponentFieldsRef.current.push(field);

        const mountOptions = {
          el,
          field,
          initialValue,
          disabled,
          t,
          onSave: (value: string) => {
            formComponentValuesRef.current[field.id] = value;
          },
          registerFlush: (flush: () => void) => formComponentFlushersRef.current.push(flush),
        };
        if (field.type === 'address-input') {
          mountFormComponentAddress(mountOptions);
        } else {
          mountFormComponentTextInput(mountOptions);
        }
      });
    };

    useImperativeHandle(ref, () => ({
      collect: () => {
        if (!contentRef.current) {
          return { ok: false, values: {}, errors: [] };
        }

        // Custom HTML mode: run the user-defined submission script if provided,
        // otherwise auto-collect values from all named form fields.
        // Pass the shadow root (not the content div) so scripts can use
        // root.getElementById / root.querySelector which are native on ShadowRoot.
        if (customHtml) {
          if (!submissionScript) {
            return { ok: false, values: {}, errors: ['No submission script defined for this custom form'] };
          }
          const root = shadowRef.current ?? contentRef.current;
          try {
            const fn = new Function('root', submissionScript);
            const result = fn(root);
            const validationError = validateCustomSubmissionResult(result);
            if (validationError) {
              return { ok: false, values: {}, errors: [validationError] };
            }
            return { ok: true, values: result, errors: [] };
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Script execution failed';
            return { ok: false, values: {}, errors: [message] };
          }
        }

        const root = contentRef.current;
        const values: Record<string, any> = {};
        const errors: string[] = [];

        // Clear previous error styling and messages
        root.querySelectorAll('.error').forEach((el) => el.classList.remove('error'));
        root.querySelectorAll('.error-text').forEach((el) => el.remove());
        // BES-9331: also reset form-component inline errors so a field fixed
        // since the last submit attempt doesn't keep showing a stale message.
        root.querySelectorAll<HTMLElement>('[data-fc-error]').forEach((el) => {
          el.style.display = 'none';
        });

        // Collect values
        const checkboxGroups = new Map<string, string[]>(); // Track checkbox groups
        const singleCheckboxes = new Set<string>(); // Track single checkboxes

        // First pass: identify checkbox groups vs single checkboxes
        root.querySelectorAll('input[type="checkbox"]').forEach((el) => {
          const checkbox = el as HTMLInputElement;
          const name = checkbox.name;
          if (!name) return;

          const allWithName = root.querySelectorAll(`input[type="checkbox"][name="${name}"]`);
          if (allWithName.length > 1) {
            if (!checkboxGroups.has(name)) {
              checkboxGroups.set(name, []);
            }
          } else {
            singleCheckboxes.add(name);
          }
        });

        // Second pass: collect values (skip guest-info inputs, handled separately)
        root.querySelectorAll('input, select, textarea').forEach((element) => {
          const el = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
          if (el.closest('[data-gi-section]')) return;
          const name = el.name;
          if (!name) return;

          const type = (el.getAttribute('type') || '').toLowerCase();
          if (type === 'checkbox') {
            if (checkboxGroups.has(name)) {
              // Checkbox group: collect checked values into array
              if ((el as HTMLInputElement).checked) {
                checkboxGroups.get(name)!.push(el.value);
              }
            } else {
              // Single checkbox: use existing logic
              values[name] = (el as HTMLInputElement).checked ? el.value || 'yes' : '';
            }
          } else if (type === 'radio') {
            if ((el as HTMLInputElement).checked) {
              values[name] = el.value;
            }
          } else {
            values[name] = el.value || '';
          }
        });

        // Add checkbox group arrays to values
        checkboxGroups.forEach((checkedValues, name) => {
          values[name] = checkedValues;
        });

        // Build required set from DOM (source of truth), union with schema to support older saves
        const required = new Set<string>();
        root.querySelectorAll('[data-form-field][data-required="true"]').forEach((wrapper) => {
          const n = (wrapper as HTMLElement).getAttribute('data-name');
          if (n) required.add(n);
        });
        (fieldsSchema || []).forEach((f) => {
          if (f.required) required.add(f.name);
        });

        // Validate required fields
        required.forEach((fieldName) => {
          // Skip names that don't exist in the DOM (stale schema)
          if (!root.querySelector(`[name="${fieldName}"]`)) return;

          const value = values[fieldName];
          const isEmpty = Array.isArray(value) ? value.length === 0 : ((value ?? '') as any).toString().trim() === '';
          if (isEmpty) {
            // Find the field wrapper and get its label text
            const fieldWrapper = root.querySelector(`[data-form-field][data-name="${fieldName}"]`);
            let fieldLabel = fieldName; // fallback to field name

            if (fieldWrapper) {
              // Try to get label from the wrapper's first label element
              const labelEl = fieldWrapper.querySelector('label');
              if (labelEl && labelEl.textContent) {
                fieldLabel = labelEl.textContent.replace(/\*\s*$/, '').trim();
              }
            }

            // If no wrapper label found, try to find associated label by 'for' attribute
            if (fieldLabel === fieldName) {
              const fieldElement = root.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
              if (fieldElement && fieldElement.id) {
                const associatedLabel = root.querySelector(`label[for="${fieldElement.id}"]`);
                if (associatedLabel && associatedLabel.textContent) {
                  fieldLabel = associatedLabel.textContent.replace(/\*\s*$/, '').trim();
                }
              }
            }

            errors.push(fieldLabel);

            // Add error styling to the field
            const wrapper = root.querySelector(`[data-form-field][data-name="${fieldName}"]`);
            const fieldType = wrapper?.getAttribute('data-form-field');

            if (fieldType === 'checkbox-group' && wrapper) {
              // For checkbox groups, style all checkboxes and add error to wrapper
              const checkboxes = wrapper.querySelectorAll('input[type="checkbox"]');
              checkboxes.forEach((checkbox) => checkbox.classList.add('error'));

              // Add inline error message
              const errorDiv = document.createElement('div');
              errorDiv.className = 'error-text';
              errorDiv.textContent = 'Please select at least one option';
              wrapper.appendChild(errorDiv);
            } else {
              // For other fields, use existing logic
              const fieldElement = root.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
              if (fieldElement) {
                fieldElement.classList.add('error');

                // Add inline error message
                const errorDiv = document.createElement('div');
                errorDiv.className = 'error-text';
                errorDiv.textContent = 'This field is required';

                // Find the best place to insert the error message
                if (wrapper) {
                  // For radio groups, append to the wrapper
                  if (wrapper.getAttribute('data-form-field') === 'radio-group') {
                    wrapper.appendChild(errorDiv);
                  } else {
                    // For other fields, insert after the field element
                    fieldElement.parentNode?.insertBefore(errorDiv, fieldElement.nextSibling);
                  }
                } else {
                  // Fallback: insert after the field element
                  fieldElement.parentNode?.insertBefore(errorDiv, fieldElement.nextSibling);
                }
              }
            }
          }
        });

        // Collect guest-info values and merge as structured JSON
        const guestInfoData = collectGuestInfoValues(root);
        Object.assign(values, guestInfoData);

        // Validate required guest-info fields
        const giErrors = validateGuestInfoRequired(root);
        errors.push(...giErrors);

        // BES-9331: registry form components. Flush pending debounced saves,
        // merge their values (keyed by component id), and validate through the
        // same shared gate the backend runs on submit so the two cannot drift.
        formComponentFlushersRef.current.forEach((flush) => flush());
        Object.assign(values, formComponentValuesRef.current);
        const formComponentFailures = findIncompleteFormComponents(
          formComponentFieldsRef.current,
          (field) => values[field.id]
        );
        formComponentFailures.forEach(({ field, reason }) => {
          errors.push(field.label);
          const wrapperEl = root.querySelector(`[${FORM_COMPONENT_ID_ATTR}="${field.id}"]`);
          const errorEl = wrapperEl?.querySelector('[data-fc-error]') as HTMLElement | null;
          if (errorEl) {
            errorEl.textContent = localizeFormComponentReason(t, reason);
            errorEl.style.display = 'block';
          }
        });

        return {
          ok: errors.length === 0,
          values,
          errors,
        };
      },

      disable: () => {
        if (!contentRef.current) return;

        const inputs = contentRef.current.querySelectorAll('input, select, textarea, button');
        inputs.forEach((input) => {
          (input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled = true;
        });
      },
    }));

    useLayoutEffect(() => {
      const host = hostRef.current;
      if (!host) return;

      // Create or reuse shadow root exactly once
      if (!shadowRef.current) {
        shadowRef.current = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

        // BASE_CSS imposes #gp-root grid layout + opinionated input/label/heading
        // styles. That's correct for GrapesJS-authored forms but stomps on
        // custom HTML where the author owns layout end-to-end. Skip it for
        // custom mode so production matches the editor's test preview.
        const templateStyle = document.createElement('style');
        templateStyle.textContent = css || '';

        const content = document.createElement('div');
        contentRef.current = content;

        if (customHtml) {
          shadowRef.current.append(templateStyle, content);
        } else {
          const baseStyle = document.createElement('style');
          baseStyle.textContent = BASE_CSS;
          const giStyle = document.createElement('style');
          giStyle.textContent = GUEST_INFO_CSS;
          shadowRef.current.append(baseStyle, giStyle, templateStyle, content);
        }
      }

      // Update content on html/css changes
      if (contentRef.current) {
        let finalHtml: string;

        if (customHtml) {
          // Custom HTML mode: inject raw HTML without DOMPurify sanitization.
          // This is authored by the host user (dashboard admin), same trust model as GrapesJS.
          finalHtml = html;
        } else {
          // Standard mode: sanitize HTML
          finalHtml = DOMPurify.sanitize(html, {
            ALLOWED_TAGS: [
              'div',
              'span',
              'p',
              'h1',
              'h2',
              'h3',
              'h4',
              'h5',
              'h6',
              'input',
              'select',
              'textarea',
              'option',
              'label',
              'fieldset',
              'legend',
              'form',
              'br',
              'strong',
              'em',
              'b',
              'i',
              'u',
              'small',
              'ul',
              'ol',
              'li',
              'table',
              'tr',
              'td',
              'th',
              'thead',
              'tbody',
              'a',
              'img',
            ],
            ALLOWED_ATTR: [
              'class',
              'id',
              'name',
              'type',
              'value',
              'placeholder',
              'required',
              'checked',
              'selected',
              'disabled',
              'readonly',
              'multiple',
              'rows',
              'cols',
              'min',
              'max',
              'step',
              'pattern',
              'title',
              'for',
              'style',
              'href',
              'src',
              'alt',
              'target',
              'rel',
              'data-*',
              'aria-*',
              'role',
            ],
            FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
            FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur'],
          });
        }

        // Standard mode: wrap in #gp-root for BASE_CSS grid layout.
        // Custom mode: inject raw HTML without extra wrapper — the author owns layout.
        const wrappedHtml = customHtml ? finalHtml : `<div id="gp-root">${finalHtml}</div>`;
        contentRef.current.innerHTML = wrappedHtml || '';

        // Custom HTML mode: manually execute <script> tags since innerHTML doesn't run them.
        // Scripts are wrapped so that `document.getElementById` / `querySelector` /
        // `querySelectorAll` resolve against the shadow root instead of the outer document.
        // Top-level function declarations are rewritten to window assignments so that
        // inline event handlers (onchange, onclick, etc.) can find them on the global scope,
        // while the proxied `document` is captured in their closure.
        if (customHtml && shadowRef.current) {
          const shadow = shadowRef.current;
          const scripts = contentRef.current.querySelectorAll('script');
          scripts.forEach((oldScript) => {
            const code = oldScript.textContent || '';
            if (!code.trim()) return;
            try {
              // Rewrite top-level named function declarations to window assignments
              // so inline event handlers (onchange="fn()") can resolve them globally.
              // The proxied `document` parameter is still captured via closure.
              const rewritten = code.replace(/^function\s+(\w+)\s*\(/gm, 'window.$1 = function(');

              const fn = new Function('document', rewritten);
              // Build a thin proxy that forwards DOM-query calls to the shadow root
              // while keeping everything else on the real document.
              const docProxy = new Proxy(document, {
                get(target, prop, receiver) {
                  if (prop === 'getElementById') return (id: string) => shadow.getElementById(id);
                  if (prop === 'querySelector') return (sel: string) => shadow.querySelector(sel);
                  if (prop === 'querySelectorAll') return (sel: string) => shadow.querySelectorAll(sel);
                  if (prop === 'addEventListener')
                    return (shadow as unknown as EventTarget).addEventListener.bind(shadow);
                  if (prop === 'createElement') return document.createElement.bind(document);
                  const value = Reflect.get(target, prop, receiver);
                  return typeof value === 'function' ? value.bind(target) : value;
                },
              });
              fn(docProxy);
            } catch (err) {
              console.warn('[FormRenderer] Custom script error:', err);
            }
            oldScript.remove();
          });
        } else if (!customHtml) {
          // Hydrate custom components after HTML injection (only for standard mode)
          hydrateCustomComponents(contentRef.current, { initialValues, numOfAdults, maxAccompanyingGuests });
          mountFormComponents(contentRef.current);
        }

        // Apply initial values (prefill)
        if (initialValues) {
          const root = contentRef.current;
          Object.entries(initialValues).forEach(([name, val]) => {
            const inputs = root.querySelectorAll(`[name="${name}"]`);
            if (inputs.length === 0) return;

            // Determine if group or single by checking DOM (match collection logic)
            const firstInput = inputs[0] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
            const type = (firstInput.getAttribute('type') || '').toLowerCase();

            if (type === 'radio') {
              inputs.forEach((el) => {
                const input = el as HTMLInputElement;
                if (input.value === String(val)) input.checked = true;
              });
            } else if (type === 'checkbox') {
              const isGroup = inputs.length > 1;
              if (isGroup) {
                // Checkbox group: expect array, check boxes whose values are in array
                const values = Array.isArray(val) ? val : [];
                inputs.forEach((el) => {
                  const input = el as HTMLInputElement;
                  input.checked = values.includes(input.value);
                });
              } else {
                // Single checkbox: expect scalar/boolean
                const input = firstInput as HTMLInputElement;
                input.checked = !!val;
              }
            } else {
              // Text inputs, selects, textareas
              (firstInput as any).value = val ?? '';
            }
          });
        }

        // Apply disabled state if needed
        if (disabled) {
          contentRef.current.querySelectorAll('input, select, textarea, button').forEach((el) => {
            (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled = true;
          });
        }
      }
    }, [html, css, customHtml]);

    // Separate effect to toggle disabled state without re-injecting HTML
    useLayoutEffect(() => {
      if (!contentRef.current) return;
      contentRef.current.querySelectorAll('input, select, textarea, button').forEach((el) => {
        (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled = disabled;
      });
    }, [disabled]);

    return (
      <div
        ref={hostRef}
        className="w-full min-h-[200px] bg-white rounded-lg border border-gray-200 p-6"
        style={{ contain: 'layout style paint' }}
      />
    );
  }
);

FormRenderer.displayName = 'FormRenderer';

export default FormRenderer;
